import type { ChatConversation, ChatMember } from "types/chat";
import type { Actor } from "types/actor";
import {
  getActorInitials,
  resolveActorAvatarUrl,
  toActorFromChatActor,
  toActorFromChatTeam,
} from "util/actorIdentity";

/**
 * Pull the most useful displayable string from a chat member record,
 * preferring a trimmed `display_name` and falling back to `username`.
 */
export const getMemberDisplayName = (
  member: Pick<ChatMember, "display_name" | "username">
): string => {
  const displayName = member.display_name?.trim();
  if (displayName) {
    return displayName;
  }
  return member.username;
};

export const getConversationTitle = (
  conversation: ChatConversation,
  fallback = "Conversation"
): string => {
  if (conversation.title?.trim()) {
    return conversation.title.trim();
  }

  if (conversation.team) {
    return conversation.team.display_name || conversation.team.teamname;
  }

  if (conversation.kind === "direct" && conversation.direct_peer) {
    return getMemberDisplayName(conversation.direct_peer);
  }

  const otherMembers = conversation.members
    .filter(
      (member) =>
        !conversation.direct_peer ||
        member.user_id !== conversation.direct_peer.user_id
    )
    .map(getMemberDisplayName);
  if (otherMembers.length > 0) {
    return otherMembers.join(", ");
  }

  return fallback;
};

export const getConversationSubtitle = (
  conversation: ChatConversation
): string => {
  if (conversation.kind === "team" && conversation.team) {
    return `Partea chat for ${conversation.team.display_name || conversation.team.teamname}`;
  }
  if (conversation.kind === "direct" && conversation.direct_peer) {
    return conversation.direct_peer.username;
  }
  if (conversation.team) {
    return `Message with ${conversation.team.display_name || conversation.team.teamname}`;
  }
  if (conversation.members.length > 1) {
    return `${conversation.members.length} members`;
  }
  return "Chat";
};

/**
 * Initials helper. Re-exported from the canonical `actorIdentity`
 * module so the chat code, the avatar primitives, and any other
 * consumer share a single implementation.
 */
export const getInitials = (value: string): string => {
  // The canonical helper takes an `Actor`, but the chat code historically
  // passes a raw display string. Wrap it in a minimal ad-hoc actor so we
  // route through the same logic without duplicating the heuristic.
  return getActorInitials({
    id: 0,
    kind: "user",
    username: value,
    displayName: value,
    avatarKey: null,
  });
};

/**
 * Resolve the canonical `Actor` for a conversation row. Prefers the
 * direct peer (DMs), then the team (group/team chats). Returns `null`
 * when neither is available, so callers can fall back to initials with
 * the conversation title.
 */
export const getConversationActor = (
  conversation: ChatConversation
): Actor | null => {
  const peer = toActorFromChatActor(conversation.direct_peer);
  if (peer) {
    return peer;
  }
  return toActorFromChatTeam(conversation.team);
};

/**
 * Convenience wrapper: composes the displayable avatar URL for a
 * conversation row using the standardized `Actor` resolver. Returns
 * `undefined` when no avatar is available so existing call sites
 * (which check `avatarUrl ? ... : initials`) keep working.
 */
export const getConversationAvatarUrl = (
  conversation: ChatConversation
): string | undefined => {
  const actor = getConversationActor(conversation);
  return resolveActorAvatarUrl(actor) ?? undefined;
};

/**
 * Pull the best timestamp to display for a conversation row, preferring
 * the latest message `created_at` and falling back to the conversation
 * `updated_at`.
 */
export const getConversationTimestamp = (
  conversation: ChatConversation
): string => {
  const latestCreatedAt = conversation.latest_message?.created_at;
  if (typeof latestCreatedAt === "string" && latestCreatedAt.trim()) {
    return latestCreatedAt;
  }
  if (
    typeof conversation.updated_at === "string" &&
    conversation.updated_at.trim()
  ) {
    return conversation.updated_at;
  }
  return "";
};
